import {
  defineField,
  defineType,
  FieldDefinition,
  FieldsetDefinition,
} from "sanity";
import {
  IHeader,
  HeaderSchema,
  IFooter,
  FooterSchema,
  GROQImageSchema,
  GROQImage,
  GROQAbTestSchema,
  IAbTest,
} from "@/sanity/lib/definitions";

export const PageSettingsType = defineType({
  name: "pageSettings",
  type: "object",
  title: "Page Settings",
  groups: [
    {
      name: "main",
      title: "Main",
      default: true,
    },
    {
      name: "seo",
      title: "SEO",
    },
    {
      name: "ab-testing",
      title: "A/B Testing",
    },
  ],
  fields: [
    defineField({
      name: "name",
      title: "Name [Internal]",
      type: "string",
      group: "main",
    }),
    defineField({
      name: "slug",
      type: "slug",
      title: "Slug",
      group: "main",
    }),
    defineField({
      name: "folder",
      title: "Folder",
      type: "reference",
      to: [{ type: "pageFolder" }],
      group: "main",
    }),
    defineField({
      name: "header",
      title: "Header",
      type: "reference",
      to: [{ type: "header" }],
      group: "main",
    }),
    defineField({
      name: "footer",
      title: "Footer",
      type: "reference",
      to: [{ type: "footer" }],
      group: "main",
    }),
    defineField({
      name: "domain",
      type: "string",
      title: "Domain",
      options: {
        list: [
          { title: "Main Domain (placeholder)", value: "main" },
          {
            title: "Landing Page Domain (get.placeholder)",
            value: "lp",
          },
        ],
      },
      initialValue: "main",
      group: "main",
    }),
    defineField({
      name: "showTopBanner",
      title: "Show top banner",
      type: "boolean",
      initialValue: false,
      group: "main",
    }),

    // SEO
    defineField({
      name: "shouldIndex",
      title: "Should Index",
      type: "boolean",
      group: "seo",
      initialValue: true,
    }),
    defineField({
      name: "metaTitle",
      title: "Meta Title",
      type: "string",
      group: "seo",
    }),
    defineField({
      name: "metaDescription",
      title: "Meta Description",
      type: "text",
      group: "seo",
    }),
    defineField({
      name: "metaImage",
      title: "Meta Image",
      type: "image",
      group: "seo",
    }),
    defineField({
      name: "metaKeywords",
      title: "Meta Keywords",
      type: "array",
      of: [{ type: "string" }],
      group: "seo",
    }),
    defineField({
      name: "categories",
      title: "Categories",
      type: "array",
      of: [{ type: "string" }],
      group: "seo",
    }),

    // A/B Testing
    defineField({
      name: "abTest",
      title: "A/B Test",
      type: "abTest",
      group: "ab-testing",
    }),
  ],
  fieldsets: [
    {
      title: "SEO",
      name: "seo",
      options: {
        collapsible: true,
        collapsed: false,
      },
    },
  ],
});

export const definePageType = ({
  name,
  title,
  fields,
  fieldsets,
}: {
  name: string;
  title: string;
  fields?: FieldDefinition[];
  fieldsets?: FieldsetDefinition[];
}) => {
  return defineType({
    name: name,
    type: "document",
    title: title,
    groups: [
      {
        title: "Settings",
        name: "settings",
      },
      {
        title: "Fields",
        name: "fields",
      },
    ],
    fieldsets: [...(fieldsets ?? [])],
    fields: [
      defineField({
        name: "settings",
        type: "pageSettings",
        title: "Settings",
        group: "settings",
      }),
      ...(fields ?? []),
    ],
    preview: {
      select: {
        title: "settings.name",
        slug: "settings.slug.current",
        domain: "settings.domain",
      },
      prepare(selection) {
        return {
          title: `[${((selection.domain ?? "") + "").toUpperCase()}] - ${selection.title}`,
          subtitle: `${selection.slug ?? ""}`,
        };
      },
    },
  });
};

export interface IPageSettingsMetaFields {
  shouldIndex?: boolean;
  metaTitle?: string;
  metaDescription?: string;
  metaImage?: GROQImage;
  metaKeywords?: string[];
}

export interface IPageSettings extends IPageSettingsMetaFields {
  _type: "pageSettings";
  name: string;
  slug: {
    _type: "slug";
    current: string;
  };
  pageFolder?: {
    _ref: string;
    _type: "reference";
  } | null;
  header: IHeader;
  footer: IFooter;
  domain: string;
  showTopBanner: boolean;
  abTest?: IAbTest;
}

export const BasePageQuery: string = `
  settings{
    name,
    slug,
    header${HeaderSchema},
    footer${FooterSchema},
    shouldIndex,
    metaTitle,
    metaDescription,
    metaImage${GROQImageSchema},
    metaKeywords,
    domain,
    showTopBanner,
    ${GROQAbTestSchema},
  }
`;
